import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { 
    Box, Container, Divider, Grid2 as Grid, useMediaQuery, useTheme
} from "@mui/material";
import {
    Send as SendIcon,
    ArrowBack as ArrowBackIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon
} from "@mui/icons-material";
import { grey } from "@mui/material/colors";

import { AuthLibMSALTokenAcquisitionError, useMSALPayload } from "@initia-digital/auth-client-utils";
import {
    BasicLoader, StatusChip, Typography,
    WorkAndSafetyIcon,
    FilledButton,
    useSnackbar,
    FilePreview,
    Textfield,
    Comment,
    BottomSearchAppBar,
    TextButton
} from "@initia-digital/component-library";
import { baseDefinedAndNotNullNarrowing, BasicLogger } from "@initia-digital/ts-utils";
import { IPlan } from "@initia-digital/types/work-and-safety";

import { BasicAppBarPage } from "../../../../layouts/BasicAppBarPage";
import { fetchPlanPdf } from "../../../../api/query";
import { useIsAdmin, useJobSearch, usePlanDetail } from "../../../../hooks";
import { getS3Object } from "../../../../api/objectStorage";
import { deletePlanReviewComment, patchPlan, postPlanReviewComment, putPlan } from "../../../../api/mutate";
import { EmergencyDirectiveBanner } from "../../../../legacy/EmergencyDirectiveBanner";
import { DEFAULT_CREATE_REVIEW_COMMENT_STATE, useWorkAndSafetyStore } from "../../../../store";
import { EditPlanSummary } from "../../../../legacy/EditPlanSummary";
import { prodBase } from "@initia-digital/colors";


const logger = new BasicLogger({ logLevel: import.meta.env.VITE_ENV_LOWER === 'prod' ? 0 : 2, logFormat: 'BROWSER' });


const ViewPlanDetail = () => {
    // ---------------------
    const [pdfUrl, setPdfUrl] = useState<string | undefined>(undefined);
    const [isPdfLoading, setIsPdfLoading] = useState(false);
    const {
        editPlanState, updateEditPlanState,
        editNextReviewCommentState, setNewReviewComment,
    } = useWorkAndSafetyStore();
    const { status } = editPlanState ?? { status: 'DRAFT' };

    const { queueSnackbar } = useSnackbar();

    const theme = useTheme();
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));
    const isSmallerThanMd = useMediaQuery(theme.breakpoints.down('md'));

    const { jobId, planId } = useParams();
    const navigate = useNavigate();

    const authDetail = useMSALPayload();
    const isAdmin = useIsAdmin(authDetail, true);

    const searchJob = useJobSearch(authDetail);

    // DATA INTEGRATION
    const queryClient = useQueryClient();

    // PLAN DETAIL
    const { planDetailData, planDetailLoading } = usePlanDetail(authDetail, planId!, baseDefinedAndNotNullNarrowing(planId));
    const {
        id, title,
        startDate, endDate,
    } = planDetailData ?? {};

    let displayEndDate = '';
    if (baseDefinedAndNotNullNarrowing(endDate) && endDate !== '') {
        // ----------------------
        displayEndDate = DateTime.fromISO(endDate).toFormat('dd/MM/yyyy');
    }
    const durationCalculated = baseDefinedAndNotNullNarrowing(startDate) && startDate !== '' && baseDefinedAndNotNullNarrowing(endDate) && endDate !== '';
    const duration = durationCalculated ? DateTime.fromISO(endDate).endOf('day').diff(DateTime.fromISO(startDate).startOf('day'), 'days', { conversionAccuracy: 'longterm' }) : null;

    // MUTATIONS
    // ---------------------
    const updatePlan = useMutation({
        mutationFn: (variables: { planId: string, plan: IPlan, authDetail: Awaited<ReturnType<typeof useMSALPayload>>, showSnackbar?: boolean }) => { return putPlan(variables) },
        onSuccess: (planUpdate, variables) => {
            // Only show snackbar if showSnackbar is true in the variables
            if (variables.showSnackbar !== false) {
                queueSnackbar(`Plan saved`, 'success', 'Success', 3000, '1rem');
            }
            // Also invalidate any plan lists that might be affected
            queryClient.invalidateQueries({ queryKey: ['plan', planUpdate.id] });
            queryClient.invalidateQueries({ queryKey: ['job', planUpdate.jobId, 'plan'] });
            queryClient.invalidateQueries({ queryKey: ['previousMonthPlanList'] });
            queryClient.invalidateQueries({ queryKey: ['currentMonthPlanList'] });
            queryClient.invalidateQueries({ queryKey: ['nextMonthPlanList'] });

            updateEditPlanState({
                ...planUpdate
            });
        },
        onError: (error) => {
            // ----------------------------
            if (
                error instanceof AuthLibMSALTokenAcquisitionError
                || ['Unauthorized', 'Could not get fresh ID token'].includes(error.message)
            ) {
                // --------------------------------------------
                queueSnackbar(`We need to be sure it's really you. Redirecting you to login`, 'error', 'Unauthorised');
                const { msal } = authDetail;
                msal.instance.loginRedirect({
                    account: msal.accounts.length > 0 ? msal.accounts[0] : undefined,
                    scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"]
                });
            }
            else if (error instanceof Error) {
                logger.error('Error saving plan', { error });
            }
            logger.error('Error saving plan');
            queueSnackbar('Plan could not be saved', 'error', 'Error saving plan', 3000, '1rem');
        }
    });

    const patchPlanMutation = useMutation({
        mutationFn: (variables: { 
            planId: string, 
            patches: {
                op: "add" | "remove" | "replace" | "move" | "copy" | "test"
                path: string
                value?: unknown
                from?: string
            }[], 
            notify: boolean, 
            authDetail: Awaited<ReturnType<typeof useMSALPayload>>,
            successMessage?: string,
            errorMessage?: string
        }) => {
            return patchPlan({
                planId: variables.planId,
                notify: variables.notify,
                patches: variables.patches,
                authDetail: variables.authDetail
            });
        },
        onSuccess: (planUpdate, variables) => {
            if (variables.successMessage) {
                queueSnackbar(variables.successMessage, 'success', 'Success', 3000, '1rem');
            }
            queryClient.invalidateQueries({ queryKey: ['plan', variables.planId] });
            queryClient.invalidateQueries({ queryKey: ['job', planUpdate.jobId, 'plan'] });
            queryClient.invalidateQueries({ queryKey: ['previousMonthPlanList'] });
            queryClient.invalidateQueries({ queryKey: ['currentMonthPlanList'] });
            queryClient.invalidateQueries({ queryKey: ['nextMonthPlanList'] });
            
            updateEditPlanState({
                ...planUpdate
            });
        },
        onError: (error, variables) => {
            if (
                error instanceof AuthLibMSALTokenAcquisitionError
                || ['Unauthorized', 'Could not get fresh ID token'].includes(error.message)
            ) {
                queueSnackbar(`We need to be sure it's really you. Redirecting you to login`, 'error', 'Unauthorised');
                const { msal } = authDetail;
                msal.instance.loginRedirect({
                    account: msal.accounts.length > 0 ? msal.accounts[0] : undefined,
                    scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"]
                });
            }
            else if (error instanceof Error) {
                logger.error('Error patching plan', { error });
            }
            logger.error('Error patching plan');
            const errorMessage = variables.errorMessage || 'Plan could not be updated';
            queueSnackbar(errorMessage, 'error', 'Error updating plan', 3000, '1rem');
        }
    });

    // EFFECTS
    // Initialize edit plan state from plan detail
    // ---------------------
    useEffect(() => {
        // When you land on the edit-plan page, you want to load the plan detail fresh on all occasions.
        // 1. The jobId or planId has changed (i.e. this is a different plan to what is already held in state)
        if (
            baseDefinedAndNotNullNarrowing(jobId) && baseDefinedAndNotNullNarrowing(planId)
            && planDetailData && !planDetailLoading
        ) {
            // ---------------------
            updateEditPlanState({
                ...planDetailData
            });
        }
    }, [planDetailData, jobId, planId, planDetailLoading, updateEditPlanState]);


    useEffect(() => {
        const loadFileUrl = async () => {
            if (!pdfUrl && !isPdfLoading) {

                const loadPdfUrl = async () => {
                    setIsPdfLoading(true);
                    const planPdf = await fetchPlanPdf(authDetail, jobId!, planId!);
                    const { path } = planPdf;

                    if (
                        !baseDefinedAndNotNullNarrowing(path)
                    ) throw new Error('PDF file could not be generated');

                    const response = await getS3Object(authDetail, { path });
                    // ---------------------
                    if (response?.buffer) {
                        try {
                            const blob = new Blob([response.buffer], { type: response.mimeType ?? 'application/octet-stream' });
                            const url = URL.createObjectURL(blob);
                            return url;
                        } catch (error) {
                            console.error('Error creating object URL:', error);
                            queueSnackbar(`Error processing plan: ${planId ?? 'unknown id'}`, 'error', 'Error', 3000, '1rem');
                        }
                        finally {
                            setIsPdfLoading(false);
                        }
                    }
                    else {
                        setIsPdfLoading(false);
                        throw new Error('File upload is missing buffer');
                    }
                }

                setIsPdfLoading(true);
                try {
                    const loadedUrl = await loadPdfUrl();
                    setPdfUrl(loadedUrl);
                } catch (error) {
                    console.error('Failed to load file URL:', error);
                } finally {
                    setIsPdfLoading(false);
                }
            }
        };

        loadFileUrl();
    }, [authDetail, pdfUrl, isPdfLoading, jobId, planId, queueSnackbar]);

    const handleSave = async (directPlanProvision?: IPlan) => {
        // ----------------------------------------------
        if (!baseDefinedAndNotNullNarrowing(planId)) {
            throw new Error('Plan ID could not be picked up');
        }

        // DEFAULT behaviour is to use the editPlanState
        let planUpdate = editPlanState;
        if (baseDefinedAndNotNullNarrowing(directPlanProvision)) {
            // -------------------------------------
            planUpdate = directPlanProvision;
        }

        await updatePlan.mutateAsync({
            planId,
            plan: {
                ...planUpdate,
                jobId
            },
            authDetail,
            showSnackbar: true
        });
    }


    return (
        <BasicAppBarPage bgColor={"white"}>
            <Container maxWidth="xl" sx={{ px: !isSmallerThanMd ? "25px" : "10px" }}>
                <EmergencyDirectiveBanner />
                <Grid
                    container
                    columnSpacing={1.5} rowSpacing={2} alignItems="stretch"
                    mb="32px"
                >
                    <Grid
                        size={{ xs: 12, md: 6, lg: 5 }}
                        height="auto"
                        border="1px solid lightgrey" borderRadius="5px"
                    >
                        {
                            planDetailLoading && (
                                <BasicLoader
                                    size={50}
                                />
                            )
                        }
                        {
                            !planDetailLoading &&
                            baseDefinedAndNotNullNarrowing(planDetailData) && (
                                <Box
                                    sx={{
                                        p: '8px 16px',
                                        paddingBottom: '4px',
                                    }}
                                >
                                    <Box
                                        pt="15px" pb="10px" px="10px"
                                        flex={1} display="flex" flexDirection="column"
                                    >
                                        <Box display="flex" mb="10px">
                                            <TextButton
                                                color={theme.palette.primary.contrastText}
                                                hoverBackgroundColor={theme.palette.primary.main}
                                                onClick={() => {
                                                    navigate(`/job/${jobId}`)
                                                }}
                                                startIcon={<ArrowBackIcon />}
                                            >
                                                {`Back to ${jobId}`}
                                            </TextButton>
                                        </Box>
                                        <Box
                                            flex={1} display="flex" flexDirection="row" alignItems="center"
                                        >
                                            <Box
                                                alignSelf="stretch"
                                                display="flex" flexDirection="column" alignItems="center"
                                            >
                                                <WorkAndSafetyIcon
                                                    size={isSmallerThanSm ? 80 : 80}
                                                    type="plan_document"
                                                />
                                            </Box>
                                            <Box
                                                flex={1}
                                                display='flex' flexDirection="column"
                                                px="15px"
                                            >
                                                <Box sx={{ lineHeight: 0 }}>
                                                    <Typography
                                                        fontSize="0.8rem"
                                                        fontWeight={700}
                                                    >
                                                        {`${jobId}${baseDefinedAndNotNullNarrowing(title) && title !== '' ? ` - ${title}` : ' - Untitled Plan'}`}
                                                    </Typography>
                                                </Box>
                                                {
                                                    baseDefinedAndNotNullNarrowing(startDate) && (
                                                        <Box sx={{ lineHeight: 0 }}>
                                                            <Typography
                                                                fontSize={"0.6rem"}
                                                                color={theme.palette.primary.contrastText}
                                                                fontWeight={300}
                                                            >
                                                                {`From ${DateTime.fromISO(startDate).toFormat('dd/MM/yyyy')}${displayEndDate !== '' ? ` to ${displayEndDate}` : ''} ${durationCalculated ? ` (${Math.round(duration!.days)} day${Math.round(duration!.days) === 1 ? '' : 's'})` : ''}`}
                                                            </Typography>
                                                        </Box>
                                                    )
                                                }
                                                {
                                                    baseDefinedAndNotNullNarrowing(status) && (
                                                        <Box
                                                            py="2px"
                                                            display="flex" flexDirection="row" alignItems="center"
                                                        >
                                                            <StatusChip
                                                                type="plan"
                                                                id={baseDefinedAndNotNullNarrowing(id) ? id : ''}
                                                                status={status}
                                                                isAdmin={isAdmin}
                                                                handleStatusChange={status === 'APPROVED' ? undefined : (planId, status, notify = false) => {
                                                                    // ----------------------------------------
                                                                    patchPlanMutation.mutateAsync({
                                                                        authDetail,
                                                                        planId,
                                                                        patches: [
                                                                            {
                                                                                "op": "replace",
                                                                                "path": "/status",
                                                                                "value": status
                                                                            }
                                                                        ],
                                                                        notify
                                                                    });
                                                                }}
                                                            />
                                                        </Box>
                                                    )
                                                }
                                            </Box>
                                        </Box>
                                        <Box
                                            mt="5px"
                                            py="5px"
                                            px="10px"
                                            bgcolor={grey[200]}
                                        >
                                            <Box
                                                display="flex" flexDirection="row" alignItems="center"
                                                py="2px" gap="2px"
                                            >
                                                <FilledButton
                                                    size="vsmall"
                                                    backgroundColor={theme.palette.secondary.main}
                                                    color={theme.palette.secondary.contrastText}
                                                    onClick={() => navigate(`/job/${jobId}/edit-plan/${planId}`)}
                                                >
                                                    {`EDIT`}
                                                </FilledButton>
                                                <FilledButton
                                                    size="vsmall"
                                                    backgroundColor={theme.palette.secondary.main}
                                                    color={theme.palette.secondary.contrastText}
                                                    onClick={() => navigate(`/job/${jobId}/plan/${planId}/prestart`)}
                                                >
                                                    {`NEW PRESTART`}
                                                </FilledButton>
                                            </Box>
                                        </Box>
                                        <Box
                                            mb="15px"
                                        >
                                            <Divider />
                                        </Box>
                                        
                                        {/* Comments Heads-up Section */}
                                        {baseDefinedAndNotNullNarrowing(editPlanState.reviewNotes) && editPlanState.reviewNotes.length > 0 && (
                                            <Box
                                                display="flex" flexDirection="row" justifyContent="space-between" alignItems="center"
                                                my="10px"
                                                p="12px"
                                                border={`1px solid ${prodBase.secondary.main}`}
                                                borderRadius="6px"
                                                sx={{ 
                                                    cursor: "pointer",
                                                    transition: 'all 0.2s ease-in-out',
                                                    "&:hover": { 
                                                        bgcolor: theme.palette.action.hover,
                                                        transform: 'translateY(2px)',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                    }
                                                }}
                                                onClick={() => {
                                                    const commentsSection = document.querySelector('#review-comments-section');
                                                    if (commentsSection) {
                                                        const rect = commentsSection.getBoundingClientRect();
                                                        const elementPosition = rect.top + window.pageYOffset;
                                                        const offsetPosition = elementPosition - 100;
                                                        
                                                        window.scrollTo({
                                                            top: offsetPosition,
                                                            behavior: 'smooth'
                                                        });
                                                    }
                                                }}
                                            >
                                                <Box
                                                    display="flex" flexDirection="row"
                                                    alignItems="center"
                                                    gap={1}
                                                >
                                                    <Typography
                                                        color={prodBase.secondary.main}
                                                        fontSize="0.8rem"
                                                        fontWeight={600}
                                                    >
                                                        Review Comments ({editPlanState.reviewNotes.length})
                                                    </Typography>
                                                </Box>
                                                
                                                {/* Scroll hint - anchored to the right */}
                                                <Box
                                                    display="flex"
                                                    flexDirection="row"
                                                    alignItems="center"
                                                    gap={1}
                                                    sx={{
                                                        color: prodBase.secondary.main
                                                    }}
                                                >
                                                    <Typography
                                                        fontSize="0.7rem"
                                                        fontWeight={500}
                                                    >
                                                        Click to view
                                                    </Typography>
                                                    <KeyboardArrowDownIcon 
                                                        sx={{ 
                                                            fontSize: '0.8rem'
                                                        }} 
                                                    />
                                                </Box>
                                            </Box>
                                        )}
                                        
                                        <EditPlanSummary />
                                        
                                        {/* Back to plan preview button */}
                                        <Box
                                            display="flex"
                                            my="15px"
                                        >
                                            <Box
                                                display="flex"
                                                flexDirection="row"
                                                alignItems="center"
                                                gap={1}
                                                p="8px 16px"
                                                border={`1px solid ${prodBase.secondary.main}`}
                                                borderRadius="5px"
                                                sx={{ 
                                                    cursor: "pointer",
                                                    transition: 'all 0.2s ease-in-out',
                                                    color: prodBase.secondary.main,
                                                    "&:hover": {                                                        
                                                        bgcolor: theme.palette.action.hover,
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                    }
                                                }}
                                                onClick={() => {
                                                    window.scrollTo({
                                                        top: 0,
                                                        behavior: 'smooth'
                                                    });
                                                }}
                                            >
                                                <KeyboardArrowDownIcon 
                                                    sx={{ 
                                                        fontSize: '0.9rem',
                                                        transform: 'rotate(180deg)' // Makes it point up
                                                    }} 
                                                />
                                                <Typography
                                                    fontSize="0.75rem"
                                                    fontWeight={500}
                                                >
                                                    Back to preview
                                                </Typography>
                                            </Box>
                                        </Box>
                                        
                                        <Box mt="20px" id="review-comments-section">
                                            <Box
                                                display="flex" flexDirection="column" width="100%"
                                                px="10px"
                                            >
                                                <Typography
                                                    variant="body1" fontSize={isSmallerThanSm ? "0.7rem" : "1rem"}
                                                    color={theme.palette.secondary.main} fontWeight={700}
                                                >
                                                    Review feedback
                                                </Typography>
                                            </Box>
                                            {
                                                planDetailLoading && (
                                                    <Box
                                                        display="flex" flexDirection="column" width="100%"
                                                        px="10px"
                                                    >
                                                        <BasicLoader size={50} />
                                                    </Box>
                                                )
                                            }
                                            {
                                                !planDetailLoading
                                                && baseDefinedAndNotNullNarrowing(editPlanState)
                                                && baseDefinedAndNotNullNarrowing(editPlanState.reviewNotes)
                                                && editPlanState.reviewNotes.length > 0 ? (
                                                    <Box
                                                        display="flex" flexDirection="column" width="100%"
                                                        px="10px"
                                                    >
                                                        {
                                                            [...editPlanState.reviewNotes]
                                                                .sort((a, b) => DateTime.fromISO(a.lastUpdateTimestamp || a.creationTimestamp!).toMillis() - DateTime.fromISO(b.lastUpdateTimestamp || b.creationTimestamp!).toMillis())
                                                                .map((note) => baseDefinedAndNotNullNarrowing(note?.lastUpdateTimestamp) ? (
                                                                    <Box
                                                                        key={`review-note-${note.id}`}
                                                                        my="5px"
                                                                    >
                                                                        <Comment
                                                                            message={note.notes}
                                                                            timestamp={note.lastUpdateTimestamp}
                                                                            userName={note.reviewedBy}
                                                                            userAvatar={note.reviewedByIcon ?? undefined}
                                                                            messageFontSize={'0.65rem'}
                                                                            userNameFontSize={'0.65rem'}
                                                                            timestampFontSize={'0.7rem'}
                                                                            handleDelete={async () => {
                                                                                // ---------------------
                                                                                await deletePlanReviewComment({
                                                                                    jobId: jobId!,
                                                                                    planId: planId!,
                                                                                    reviewNoteId: note.id,
                                                                                    authDetail
                                                                                });
                        
                                                                                await handleSave();                        
                                                                                await queryClient.invalidateQueries({ queryKey: ['plan', planId] });
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                ) : <></>)
                                                        }
                                                    </Box>
                                                )
                                                :
                                                <Box
                                                    display="flex" flexDirection="column" width="100%"
                                                    px="10px"
                                                >
                                                    <Typography
                                                        fontSize="0.8rem"
                                                        fontWeight={300}
                                                        color={theme.palette.primary.contrastText}
                                                    >
                                                        Get started and pass on some feedback
                                                    </Typography>
                                                </Box>
                                            }
                                            <Box
                                                display="flex" flexDirection="column" width="100%"
                                                my="10px" px="10px"
                                            >
                                                <Textfield
                                                    fontSize="0.8rem"
                                                    name="reviewComment"
                                                    label="Add a review comment"
                                                    placeholder="Add a review comment"
                                                    value={editNextReviewCommentState}
                                                    minRows={3}
                                                    handleChange={(event) => {
                                                        // ---------------------
                                                        setNewReviewComment(event.target.value);
                                                    }}
                                                />
                                            </Box>
                                            <Box
                                                display="flex"
                                                justifyContent="flex-end"
                                                width="100%"
                                                px="10px"
                                                mt="10px"
                                                mb="10px"
                                            >
                                                <FilledButton
                                                    size="small"
                                                    startIcon={<SendIcon />}
                                                    color={theme.palette.secondary.contrastText}
                                                    backgroundColor={theme.palette.secondary.main}
                                                    onClick={async () => {
                                                        // ---------------------
                                                        await postPlanReviewComment({
                                                            jobId: jobId!,
                                                            planId: planId!,
                                                            reviewComment: editNextReviewCommentState,
                                                            authDetail
                                                        });

                                                        await handleSave();
                                                        setNewReviewComment(DEFAULT_CREATE_REVIEW_COMMENT_STATE);

                                                        await queryClient.invalidateQueries({ queryKey: ['plan', planId] });
                                                    }}
                                                >
                                                    Post
                                                </FilledButton>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            )
                        }
                    </Grid>
                    <Grid
                        size={{ xs: 12, md: 6, lg: 7 }}
                        py="10px" border="1px solid lightgrey" borderRadius="5px"
                        height="auto"
                    >
                        <Box
                            display="flex" flexDirection="column" width="100%"
                            px="10px"
                        >
                            <Typography
                                variant="body1" fontSize={isSmallerThanSm ? "0.7rem" : "1rem"}
                                color={theme.palette.secondary.main} fontWeight={700}
                            >
                                Preview
                            </Typography>
                        </Box>
                        <Box
                            px="10px"
                        >
                            <Typography
                                fontSize={isSmallerThanSm ? "0.7rem" : "0.75rem"}
                                fontWeight={300}
                                color={theme.palette.primary.contrastText}
                            >
                                You can download a PDF copy by clicking 'download' below
                            </Typography>
                        </Box>
                        <Box
                            px="10px"
                        >
                            {
                                isPdfLoading ? (
                                    <Box display="flex" flexDirection="column" alignItems="center" p={2}>
                                        <Typography fontSize="0.8rem">Building a PDF for the plan</Typography>
                                        <BasicLoader size={50} />
                                    </Box>
                                ) : (
                                    <FilePreview url={pdfUrl} filename={`${DateTime.local().toFormat('yyyyMMdd_HHmmss')}-${jobId}-${baseDefinedAndNotNullNarrowing(title) && title.trim() !== '' ? title : 'Untitled work plan'}.pdf`} />
                                )
                            }
                        </Box>
                    </Grid>
                </Grid>
                <BottomSearchAppBar
                    placeholder='Search for a job'
                    label='Search for a Job'
                    labelKey='jobId'
                    secondaryKeysForDisplay={['jobId']}
                    suggestionKeysForOptionDisplay={['jobId', 'jobName']}
                    suggestionKeysToSearchOption={['jobId', 'jobName']}
                    uniqueOptionKeyProps={['jobId']}
                    handleChange={(value) => { navigate(`/job/${value.jobId}`) }}
                    searchSuggestion={searchJob}
                />
            </Container>
        </BasicAppBarPage>
    )
}

export default ViewPlanDetail;